/**
 * Teslyn's own email (batch 6: F07 client emails when a daily or weekly report is published, F14 email sent from the
 * app; decisions 508 and 553; docs/specs/cloud/app-email.md).
 *
 * - Every email the app sends for a person goes through Teslyn's mail service (Resend), from
 *   "No Reply via Teslyn" <no-reply@teslyn.au> (decision 508: no Microsoft Graph mail; decision 553: the address).
 *   Where an answer is useful the email's Reply-To is the person it is from, so a reply reaches them, never the
 *   no-reply address.
 * - F07: when a daily or weekly report is published to a project's client organisation, each of the organisation's
 *   people is told by email, once per report, with a link to Teslyn Cloud's Reports page, the PDF attached, or both
 *   (decision 884). A manager turns the emails off per project and kind; each client turns
 *   them off for themselves. A report unpublished within `REPORT_EMAIL_HOLD_S` is not emailed.
 * - F14: staff send a short message (a hazard pack link to the pilots, a report to a colleague) to people Teslyn
 *   knows (the staff register, the organisation's sign-ins, its clients' sign-ins), never to a typed address, so
 *   Teslyn's domain cannot be used to send mail to strangers.
 * - Decision 890: the sender is named after the organisation, "No Reply Thomson Airborne powered by Teslyn"
 *   (`email-sender.ts`).
 * - Decision 883: the report email's subject and message are the project's own (Set-up tab, with Teslyn's wording to
 *   start from and placeholders such as {project} and {report date}), and the publisher can change them when
 *   publishing. Decision 884: the email carries the link to Teslyn Cloud, the PDF attached, or both (link only unless
 *   the project or the publisher chooses otherwise), and a weekly report's email can carry that day's daily report
 *   too. A PDF over `APP_EMAIL_ATTACH_MAX_BYTES` is never attached: the email links to it instead and says why.
 *
 * It imports only report-label.ts and email-sender.ts (zero imports), so the notify Edge Function uses the same words
 * (tools/dev/copy-edge-shared.mjs). The database holds the same limits (`email_send`, `email_claim`;
 * supabase/tests/rls-app-email.test.ts and rls-report-email-options.test.ts check they match).
 */
import { reportLabel, periodText } from './report-label.js';
import { appEmailSenderName } from './email-sender.js';

/** People one message goes to at most (F14). */
export const APP_EMAIL_MAX_RECIPIENTS = 25;
/** Messages one person sends in 24 hours at most (F14; Resend's free plan allows 100 emails a day, decision 740). */
export const APP_EMAIL_MESSAGES_PER_DAY = 20;
export const APP_EMAIL_SUBJECT_MAX = 150;
export const APP_EMAIL_BODY_MAX = 5000;
/** Seconds a published report waits before its email goes, so a report unpublished by mistake is not emailed. */
export const REPORT_EMAIL_HOLD_S = 120;
/** Tries of one email at most, within a day of being queued. */
export const APP_EMAIL_MAX_TRIES = 5;

/** The staff roles that send messages (F14): every staff role; clients send none. */
export const APP_EMAIL_SENDER_ROLES: readonly string[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
];
/**
 * Who turns a project's report emails on or off: the people who link the project to its client organisation
 * (`project.link_client` in portal.ts; policy `project_write`).
 */
export const REPORT_EMAIL_SETTING_ROLES: readonly string[] = ['tenant_admin', 'manager', 'office_staff'];

/** The report kinds a client is emailed about (F07): the two the portal shows (`CLIENT_REPORT_KINDS`). */
export type ReportEmailKind = 'daily' | 'weekly';
export const REPORT_EMAIL_KINDS: readonly ReportEmailKind[] = ['daily', 'weekly'];

/**
 * What a report email carries (decision 884): the link to Teslyn Cloud's Reports page, the PDF attached, or both.
 */
export type ReportEmailContent = 'link' | 'pdf' | 'both';
export const REPORT_EMAIL_CONTENTS: readonly ReportEmailContent[] = ['link', 'pdf', 'both'];
export const REPORT_EMAIL_CONTENT_WORD: Readonly<Record<ReportEmailContent, string>> = {
  link: 'The link to the report in Teslyn Cloud',
  pdf: 'The PDF attached',
  both: 'The link and the PDF attached',
};
/**
 * Decision 884 read the safe way: the link only, as before, until the project's Set-up tab or the publisher chooses
 * the PDF. A link keeps the report behind the portal's sign-in (an attachment can be forwarded) and never bounces for
 * its size.
 */
export const REPORT_EMAIL_CONTENT_DEFAULT: ReportEmailContent = 'link';

/**
 * The most PDF one email carries, in bytes (15 MB, before encoding). Resend refuses an email over 40 MB after the
 * attachments' Base64 encoding (a third larger); many mail servers refuse far less (Gmail 25 MB, Outlook.com 20 MB,
 * company servers often 10 to 25 MB, all counted after encoding). 15 MB is about 20 MB encoded. A PDF that does not
 * fit is linked instead, and the email says it was too large to attach. F26048's genuine weekly reports are 2.3 MB
 * (week 41), 10.5 MB (week 44) and 39.1 MB (Teslyn's week 43, with every grid); its daily reports 1.1 to 2.9 MB.
 */
export const APP_EMAIL_ATTACH_MAX_BYTES = 15 * 1024 * 1024;

/** A project's report emails (`project_report_email`); no row: both on, Teslyn's wording, the link only. */
export interface ReportEmailSetting {
  readonly daily: boolean;
  readonly weekly: boolean;
  /** The project's subject (placeholders allowed); null or absent: Teslyn's (`REPORT_EMAIL_SUBJECT_DEFAULT`). */
  readonly subject?: string | null;
  /** The project's message (placeholders allowed); null or absent: Teslyn's (`REPORT_EMAIL_MESSAGE_DEFAULT`). */
  readonly message?: string | null;
  /** What the email carries; absent: `REPORT_EMAIL_CONTENT_DEFAULT`. */
  readonly content?: ReportEmailContent;
  /** A weekly report's email also carries the day's daily report (the publisher can change it); absent: no. */
  readonly weekly_with_daily?: boolean;
}
export const REPORT_EMAIL_DEFAULT: ReportEmailSetting = { daily: true, weekly: true };

/** Every part of a project's setting, with Teslyn's defaults where it has none. */
export interface ReportEmailSettingFull {
  readonly daily: boolean;
  readonly weekly: boolean;
  readonly subject: string | null;
  readonly message: string | null;
  readonly content: ReportEmailContent;
  readonly weekly_with_daily: boolean;
}

export function reportEmailSettingFull(
  setting: ReportEmailSetting | null | undefined,
): ReportEmailSettingFull {
  const s = setting ?? REPORT_EMAIL_DEFAULT;
  return {
    daily: s.daily,
    weekly: s.weekly,
    subject: s.subject ?? null,
    message: s.message ?? null,
    content:
      s.content && REPORT_EMAIL_CONTENTS.includes(s.content) ? s.content : REPORT_EMAIL_CONTENT_DEFAULT,
    weekly_with_daily: s.weekly_with_daily ?? false,
  };
}

// ------------------------------------------------------------------------- the words (decision 883)

/** Teslyn's subject: "F26048 Milgun: Daily report, 16 Oct 2026". */
export const REPORT_EMAIL_SUBJECT_DEFAULT = '{project}: {report}';
/** Teslyn's message; the link or attachment, the reply line and how to stop the emails are always added after it. */
export const REPORT_EMAIL_MESSAGE_DEFAULT =
  'Hello {name},\n\n{organisation} has published a report on {project} for {client}: {report}.';
/** The longest message a project or publisher writes (the subject's is `APP_EMAIL_SUBJECT_MAX`). */
export const REPORT_EMAIL_MESSAGE_MAX = 2000;

/** The placeholders a report email's subject and message can use. */
export type ReportEmailPlaceholder =
  'project' | 'project number' | 'report' | 'report date' | 'client' | 'organisation' | 'publisher' | 'name';
export const REPORT_EMAIL_PLACEHOLDERS: readonly {
  readonly key: ReportEmailPlaceholder;
  readonly words: string;
}[] = [
  { key: 'project', words: 'the project number and name, e.g. F26048 Milgun' },
  { key: 'project number', words: 'the project number, e.g. F26048' },
  { key: 'report', words: 'the report and its date, e.g. Daily report, 16 Oct 2026' },
  { key: 'report date', words: "the report's date, e.g. 16 Oct 2026 (a weekly report: Week 42, 2026)" },
  { key: 'client', words: 'the client organisation, e.g. Benz Mining Corp' },
  { key: 'organisation', words: 'your organisation, e.g. Thomson Airborne' },
  { key: 'publisher', words: 'who published the report' },
  { key: 'name', words: 'the name of the person it goes to (left out when Teslyn has no name)' },
];

const PLACEHOLDER = /([ \t]?)\{([^{}\n]{1,40})\}/g;
const placeholderKey = (raw: string): string => raw.trim().toLowerCase().replace(/\s+/g, ' ');

/** The placeholders' values for one email. */
export type ReportEmailValues = Readonly<Record<ReportEmailPlaceholder, string>>;

/**
 * The subject or message with its placeholders filled. An empty value takes the space before it too, so
 * "Hello {name}," reads "Hello," for someone without a name. A placeholder Teslyn does not know is left as written
 * (the Set-up tab and the publish dialog refuse one before it is saved).
 */
export function fillReportEmailWords(template: string, values: ReportEmailValues): string {
  return template.replace(PLACEHOLDER, (whole: string, space: string, raw: string) => {
    const key = placeholderKey(raw);
    if (!Object.prototype.hasOwnProperty.call(values, key)) return whole;
    const v = values[key as ReportEmailPlaceholder];
    return v === '' ? '' : `${space}${v}`;
  });
}

/** The placeholders in a text that Teslyn does not know, as written. */
export function unknownPlaceholders(text: string): string[] {
  const known = new Set<string>(REPORT_EMAIL_PLACEHOLDERS.map((p) => p.key));
  const out: string[] = [];
  for (const m of text.matchAll(PLACEHOLDER)) {
    const raw = m[2] ?? '';
    if (!known.has(placeholderKey(raw)) && !out.includes(`{${raw}}`)) out.push(`{${raw}}`);
  }
  return out;
}

const normaliseWords = (s: string): string => s.replace(/\r\n?/g, '\n').trim();

/**
 * What is wrong with a subject and message before they are saved or used (none: they can be). The database checks
 * the lengths too.
 */
export function reportEmailWordingProblems(w: {
  readonly subject: string;
  readonly message: string;
}): string[] {
  const out: string[] = [];
  const subject = normaliseWords(w.subject);
  const message = normaliseWords(w.message);
  if (subject === '') out.push('Write a subject, or choose "Use Teslyn\'s wording".');
  else if (subject.includes('\n')) out.push('Keep the subject on one line.');
  else if (subject.length > APP_EMAIL_SUBJECT_MAX)
    out.push(`Shorten the subject to ${APP_EMAIL_SUBJECT_MAX} characters.`);
  if (message === '') out.push('Write the message, or choose "Use Teslyn\'s wording".');
  else if (message.length > REPORT_EMAIL_MESSAGE_MAX)
    out.push(`Shorten the message to ${REPORT_EMAIL_MESSAGE_MAX} characters.`);
  const unknown = [...unknownPlaceholders(subject), ...unknownPlaceholders(message)].filter(
    (x, i, all) => all.indexOf(x) === i,
  );
  if (unknown.length > 0)
    out.push(
      `Teslyn does not know ${unknown.join(', ')}. Use ${REPORT_EMAIL_PLACEHOLDERS.map((p) => `{${p.key}}`).join(', ')}.`,
    );
  return out;
}

/**
 * The subject or message as it is kept: null when it is the wording it would be anyway (`fallback`: Teslyn's, or the
 * project's), so a later change of that wording still reaches it; otherwise the text with plain line endings.
 */
export function reportEmailWordsToKeep(text: string, fallback: string): string | null {
  const t = normaliseWords(text);
  return t === '' || t === normaliseWords(fallback) ? null : t;
}

/** The project's subject and message as they start in the boxes: its own, else Teslyn's. */
export function reportEmailWording(setting: ReportEmailSetting | null | undefined): {
  readonly subject: string;
  readonly message: string;
} {
  const s = reportEmailSettingFull(setting);
  return {
    subject: s.subject ?? REPORT_EMAIL_SUBJECT_DEFAULT,
    message: s.message ?? REPORT_EMAIL_MESSAGE_DEFAULT,
  };
}

// ------------------------------------------------------------------------- the publisher's choice (883, 884)

/**
 * What the publisher chose for one report's email (`report_publication.email_options`, written with Publish).
 * subject and message null: the project's wording (else Teslyn's). daily_report_id: a weekly report's email also
 * carries this daily report (one published to the client).
 */
export interface ReportEmailOptions {
  readonly send: boolean;
  readonly subject: string | null;
  readonly message: string | null;
  readonly content: ReportEmailContent;
  readonly daily_report_id: string | null;
}

/** A report as the publish dialog reads it. */
export interface PublishableReport {
  readonly id: string;
  readonly kind: string;
  readonly period: string;
  readonly revision: number;
  /** Published to the client now. */
  readonly published: boolean;
}

/**
 * The daily report a weekly report's email carries by default. "That day's" (decision 884) is read as the latest
 * daily report published to the client, as a rule the one published with the weekly: the latest date, its highest
 * revision. The database picks the same for a weekly published without a choice (a newer revision published in the
 * old one's place; rls-report-email-options.test.ts).
 */
export function latestPublishedDaily(reports: readonly PublishableReport[]): PublishableReport | null {
  let best: PublishableReport | null = null;
  for (const r of reports) {
    if (r.kind !== 'daily' || !r.published) continue;
    if (!best || r.period > best.period || (r.period === best.period && r.revision > best.revision)) best = r;
  }
  return best;
}

/** The publish dialog's starting choice: the project's setting for the report's kind. */
export function reportEmailOptionsDefault(
  setting: ReportEmailSetting | null | undefined,
  kind: string,
  reports: readonly PublishableReport[] = [],
): ReportEmailOptions {
  const s = reportEmailSettingFull(setting);
  return {
    send: reportEmailOn(s, kind),
    subject: null,
    message: null,
    content: s.content,
    daily_report_id:
      kind === 'weekly' && s.weekly_with_daily ? (latestPublishedDaily(reports)?.id ?? null) : null,
  };
}

/**
 * The publisher's choice as it is kept with Publish: the words null where they equal the project's (so the email
 * reads exactly what the project says), no daily report but for a weekly one.
 */
export function reportEmailOptionsToKeep(
  o: {
    readonly send: boolean;
    readonly subject: string;
    readonly message: string;
    readonly content: ReportEmailContent;
    readonly daily_report_id: string | null;
  },
  setting: ReportEmailSetting | null | undefined,
  kind: string,
): ReportEmailOptions {
  const w = reportEmailWording(setting);
  return {
    send: o.send,
    subject: reportEmailWordsToKeep(o.subject, w.subject),
    message: reportEmailWordsToKeep(o.message, w.message),
    content: o.content,
    daily_report_id: kind === 'weekly' ? o.daily_report_id : null,
  };
}

/** What happens to the client's email of a report just published, in words ("Its people are emailed …"). */
export function reportEmailNotice(
  options: Pick<ReportEmailOptions, 'send' | 'content' | 'daily_report_id'>,
): string {
  if (!options.send) return 'Not emailed, as you chose.';
  const carries =
    options.content === 'link'
      ? 'with the link'
      : options.content === 'pdf'
        ? 'with the PDF attached'
        : 'with the link and the PDF attached';
  const daily = options.daily_report_id ? ', the daily report too' : '';
  return `Its people are emailed in about 2 minutes, ${carries}${daily}, unless they turned report emails off.`;
}

/** What the person who published reads afterwards. */
export function reportPublishedNotice(n: {
  readonly label: string;
  readonly clientOrgName: string | null;
  readonly options: Pick<ReportEmailOptions, 'send' | 'content' | 'daily_report_id'>;
}): string {
  return `${n.label} published to ${n.clientOrgName ?? 'the client'}. ${reportEmailNotice(n.options)}`;
}

/** Bytes in words: "39.1 MB", "820 kB". */
export function emailSizeText(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/** A PDF an email could attach: the report's own, or the daily report a weekly one carries. */
export interface ReportEmailPdf {
  readonly key: 'report' | 'daily';
  readonly fileName: string;
  readonly sizeBytes: number;
}

/**
 * Which PDFs an email attaches: none for the link only; otherwise each in turn (the report first, then the daily
 * report) while the total stays within `max`. The rest are too large: the email links to them instead.
 */
export function reportEmailAttachments(
  content: ReportEmailContent,
  pdfs: readonly ReportEmailPdf[],
  max: number = APP_EMAIL_ATTACH_MAX_BYTES,
): { readonly attach: ReportEmailPdf[]; readonly tooLarge: ReportEmailPdf[] } {
  if (content === 'link') return { attach: [], tooLarge: [] };
  const attach: ReportEmailPdf[] = [];
  const tooLarge: ReportEmailPdf[] = [];
  let total = 0;
  for (const p of pdfs) {
    if (p.sizeBytes > 0 && total + p.sizeBytes <= max) {
      attach.push(p);
      total += p.sizeBytes;
    } else tooLarge.push(p);
  }
  return { attach, tooLarge };
}

/** Whether a published report of this kind is emailed to the project's client organisation. */
export function reportEmailOn(setting: ReportEmailSetting | null | undefined, kind: string): boolean {
  if (kind !== 'daily' && kind !== 'weekly') return false;
  return (setting ?? REPORT_EMAIL_DEFAULT)[kind];
}

/** "Daily and weekly reports are emailed", in words, for the project's setting. */
export function reportEmailSettingWords(setting: ReportEmailSetting | null | undefined): string {
  const s = setting ?? REPORT_EMAIL_DEFAULT;
  if (s.daily && s.weekly) return 'The client is emailed when a daily or weekly report is published.';
  if (s.daily) return 'The client is emailed when a daily report is published, not a weekly one.';
  if (s.weekly) return 'The client is emailed when a weekly report is published, not a daily one.';
  return 'The client is not emailed when a report is published.';
}

export interface AppEmail {
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

const clip = (s: string, n: number): string => (s.length <= n ? s : `${s.slice(0, n - 1)}…`);

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Plain text paragraphs as HTML: each paragraph a `<p>`, single line breaks kept, a bare https link made a link. */
function paragraphsHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .map(
      (p) =>
        `<p>${escapeHtml(p)
          .replace(/(https:\/\/[^\s<]+)/g, '<a href="$1">$1</a>')
          .replace(/\n/g, '<br>')}</p>`,
    )
    .join('\n');
}

/**
 * Teslyn Cloud's Reports page at the address the email links to, on the report's project when it is known
 * (`&project=<id>`, R198: the page opens with that project chosen); null when the function has no address set.
 */
export function reportsPageUrl(cloudUrl: string, projectId?: string | null): string | null {
  const base = cloudUrl.trim().replace(/\/+$/, '');
  if (!/^https:\/\/[^\s/]+/.test(base)) return null;
  return projectId
    ? `${base}/?page=reports&project=${encodeURIComponent(projectId)}`
    : `${base}/?page=reports`;
}

/** A report's kind (the `report.kind` check), declared here so the Edge Functions' copy needs no other types. */
export type AppReportKind = 'daily' | 'weekly' | 'final' | 'qc';

export interface ReportEmailFacts {
  readonly kind: AppReportKind;
  readonly period: string;
  readonly revision: number;
  readonly projectNumber: string;
  readonly projectTitle: string;
  /** The organisation that published the report (the tenant). */
  readonly tenantName: string;
  /** The client organisation the email goes to. */
  readonly clientOrgName: string;
  /** The person it goes to, '' when their sign-in has no name. */
  readonly recipientName: string;
  /** Who published it (the Reply-To), null when nobody's address is known: the email then says not to reply. */
  readonly publisherName: string | null;
  /** Teslyn Cloud's address ('' when not set: the email then names the page in words). */
  readonly cloudUrl: string;
  /** The report's project: the link opens Reports on it (R198); absent or null: the page's own choice. */
  readonly projectId?: string | null;
  /** The subject with placeholders (decision 883); null or absent: Teslyn's. */
  readonly subjectTemplate?: string | null;
  /** The message with placeholders (decision 883); null or absent: Teslyn's. */
  readonly messageTemplate?: string | null;
  /** What the email carries (decision 884); absent: the link only. */
  readonly content?: ReportEmailContent;
  /** The report's PDF (its name and size), for an attachment; absent: none known (linked). */
  readonly pdf?: { readonly fileName: string; readonly sizeBytes: number } | null;
  /** The daily report a weekly report's email carries too (decision 884), still published to the client. */
  readonly daily?: {
    readonly period: string;
    readonly revision: number;
    readonly pdf: { readonly fileName: string; readonly sizeBytes: number } | null;
  } | null;
}

/** A report email: its words and the PDFs to attach (`report`: the report's own, `daily`: the daily report's). */
export interface ReportEmail extends AppEmail {
  readonly attach: readonly ReportEmailPdf[];
}

/** The placeholders' values of one report email. */
export function reportEmailValues(f: ReportEmailFacts): ReportEmailValues {
  return {
    project: `${f.projectNumber} ${f.projectTitle}`.trim(),
    'project number': f.projectNumber,
    report: reportLabel({ kind: f.kind, period: f.period, revision: f.revision }),
    'report date': periodText(f.period),
    client: f.clientOrgName,
    organisation: f.tenantName,
    publisher: f.publisherName ?? f.tenantName,
    name: f.recipientName.trim(),
  };
}

const listWords = (xs: readonly string[]): string =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]!}`;

/**
 * The email telling a client a report is published (F07): the project's or publisher's subject and message
 * (decision 883, Teslyn's wording by default), then what Teslyn always adds: that a revision replaces the earlier
 * copy, the daily report a weekly one carries, the PDFs attached and those too large to attach, the link (decision
 * 884), who to reply to and how to stop the emails.
 */
export function reportPublishedEmail(f: ReportEmailFacts): ReportEmail {
  const values = reportEmailValues(f);
  const content = f.content ?? REPORT_EMAIL_CONTENT_DEFAULT;
  const dailyLabel = f.daily
    ? reportLabel({ kind: 'daily', period: f.daily.period, revision: f.daily.revision })
    : null;
  const pdfs: ReportEmailPdf[] = [];
  const unknown: string[] = [];
  if (content !== 'link') {
    if (f.pdf) pdfs.push({ key: 'report', ...f.pdf });
    else unknown.push(values.report);
    if (f.daily) {
      if (f.daily.pdf) pdfs.push({ key: 'daily', ...f.daily.pdf });
      else unknown.push(dailyLabel!);
    }
  }
  const { attach, tooLarge } = reportEmailAttachments(content, pdfs);
  const labelOf = (p: ReportEmailPdf): string => (p.key === 'report' ? values.report : dailyLabel!);
  const url = reportsPageUrl(f.cloudUrl, f.projectId);
  const both = f.daily !== null && f.daily !== undefined;
  const linked = content !== 'pdf' || attach.length < pdfs.length || unknown.length > 0;
  const message = fillReportEmailWords(f.messageTemplate ?? REPORT_EMAIL_MESSAGE_DEFAULT, values).trim();
  const paragraphs = [
    message,
    f.revision > 1 ? 'It replaces the earlier copy of the same report.' : '',
    dailyLabel ? `With it, the day's report: ${dailyLabel}.` : '',
    attach.length > 0
      ? `Attached: ${listWords(attach.map((p) => `${p.fileName} (${emailSizeText(p.sizeBytes)})`))}.`
      : '',
    tooLarge.length > 0
      ? `Too large to attach (an email carries at most ${emailSizeText(APP_EMAIL_ATTACH_MAX_BYTES)}): ${listWords(
          tooLarge.map((p) => `${labelOf(p)}, ${emailSizeText(p.sizeBytes)}`),
        )}.`
      : '',
    linked
      ? url
        ? `Open ${both ? 'them' : 'it'} in Teslyn Cloud (sign in first): ${url}`
        : `Open ${both ? 'them' : 'it'} in Teslyn Cloud: sign in, then choose Reports.`
      : '',
    f.publisherName
      ? `Questions about the report? Reply to this email to reach ${f.publisherName}.`
      : `This address is not read: please contact ${f.tenantName} directly with any questions.`,
    `You receive this because you can see ${f.projectNumber}'s reports in ${f.tenantName}'s client portal. To stop these emails, open Teslyn Cloud, My sign-in, Report emails.`,
  ].filter((p) => p !== '');
  const text = paragraphs.join('\n\n');
  const subject = fillReportEmailWords(f.subjectTemplate ?? REPORT_EMAIL_SUBJECT_DEFAULT, values)
    .replace(/\s+/g, ' ')
    .trim();
  return {
    subject: clip(subject || `${values.project}: ${values.report}`, APP_EMAIL_SUBJECT_MAX),
    text,
    html: paragraphsHtml(text),
    attach,
  };
}

export interface MessageFacts {
  /** The sender's name (their sign-in's name, else their address). */
  readonly senderName: string;
  readonly tenantName: string;
  readonly subject: string;
  readonly body: string;
}

/** A message a person sends from the app (F14): their words, then who sent it and how to answer. */
export function appMessageEmail(f: MessageFacts): AppEmail {
  const footer = `Sent by ${f.senderName} of ${f.tenantName} through Teslyn. Reply to this email to answer ${f.senderName}.`;
  const text = `${f.body.trim()}\n\n--\n${footer}`;
  return {
    subject: clip(f.subject.trim(), APP_EMAIL_SUBJECT_MAX),
    text,
    html: `${paragraphsHtml(f.body.trim())}\n<p><small>${escapeHtml(footer)}</small></p>`,
  };
}

/** What is wrong with a message before it is sent, in words (none: it can go). The database checks the same. */
export function appMessageProblems(m: {
  readonly subject: string;
  readonly body: string;
  readonly recipients: number;
}): string[] {
  const out: string[] = [];
  if (m.recipients === 0) out.push('Choose at least one person to send it to.');
  if (m.recipients > APP_EMAIL_MAX_RECIPIENTS)
    out.push(`Choose at most ${APP_EMAIL_MAX_RECIPIENTS} people for one message.`);
  const subject = m.subject.trim();
  if (subject === '') out.push('Write a subject.');
  else if (subject.length > APP_EMAIL_SUBJECT_MAX)
    out.push(`Shorten the subject to ${APP_EMAIL_SUBJECT_MAX} characters.`);
  const body = m.body.trim();
  if (body === '') out.push('Write the message.');
  else if (body.length > APP_EMAIL_BODY_MAX)
    out.push(`Shorten the message to ${APP_EMAIL_BODY_MAX} characters.`);
  return out;
}

/** A person a message can go to: a staff register person, or a sign-in (staff or client). */
export interface EmailRecipientChoice {
  /** `person:<id>` or `user:<id>`. */
  readonly key: string;
  readonly name: string;
  /** Why they are listed: "Pilot on the crew today", "Benz Mining Corp (client)". */
  readonly why: string;
  /** False when Teslyn has no email address for them (listed, not choosable). */
  readonly hasEmail: boolean;
}

/** A staff register person as the choice reads them (`person`). */
export interface RecipientPerson {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly user_id: string | null;
  readonly active?: boolean;
  readonly deleted_at: string | null;
}

/** A sign-in of the organisation or of one of its clients. */
export interface RecipientSignIn {
  readonly user_id: string;
  readonly name: string;
  readonly email: string | null;
  /** The client organisation's name for a client; null for the organisation's own staff. */
  readonly client_org: string | null;
}

const CREW_ROLE_WORD: Readonly<Record<string, string>> = {
  pilot: 'Pilot on this project',
  operator: 'Operator on this project',
};

/**
 * Who a message can go to (F14), in the order the dialog lists them: the project's crew, the rest of the staff
 * register (active people), the organisation's sign-ins not in the register, then the client's people. Everyone
 * once; someone with no address is listed but cannot be chosen.
 */
export function emailRecipientChoices(input: {
  readonly people: readonly RecipientPerson[];
  readonly crew: readonly {
    readonly person_id: string;
    readonly role: string;
    readonly deleted_at: string | null;
  }[];
  readonly signIns: readonly RecipientSignIn[];
}): EmailRecipientChoice[] {
  const out: EmailRecipientChoice[] = [];
  const seenUsers = new Set<string>();
  const live = input.people.filter((p) => p.deleted_at === null);
  const emailOf = (p: RecipientPerson): boolean =>
    Boolean(p.email) || input.signIns.some((s) => s.user_id === p.user_id && s.email);
  const crewRole = new Map<string, string>();
  for (const c of input.crew)
    if (c.deleted_at === null && !crewRole.has(c.person_id))
      crewRole.set(c.person_id, CREW_ROLE_WORD[c.role] ?? 'On this project');
  const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name);
  const crewPeople = live.filter((p) => crewRole.has(p.id)).sort(byName);
  const others = live.filter((p) => !crewRole.has(p.id) && p.active !== false).sort(byName);
  for (const p of [...crewPeople, ...others]) {
    if (p.user_id) seenUsers.add(p.user_id);
    out.push({
      key: `person:${p.id}`,
      name: p.name,
      why: crewRole.get(p.id) ?? 'Staff register',
      hasEmail: emailOf(p),
    });
  }
  const staff = input.signIns.filter((s) => s.client_org === null && !seenUsers.has(s.user_id));
  const clients = input.signIns.filter((s) => s.client_org !== null);
  for (const s of [...staff].sort(byName))
    out.push({ key: `user:${s.user_id}`, name: s.name, why: 'Teslyn sign-in', hasEmail: Boolean(s.email) });
  for (const s of [...clients].sort(byName))
    out.push({
      key: `user:${s.user_id}`,
      name: s.name,
      why: `${s.client_org!} (client)`,
      hasEmail: Boolean(s.email),
    });
  return out;
}

/**
 * Who a hazard pack's "Email to pilots" ticks at the start (decisions 88, 625, 808; Teslyn Field's pack dialog): the
 * project's pilots who have an email address. Anyone else listed can be ticked by hand.
 */
export function pilotRecipientKeys(choices: readonly EmailRecipientChoice[]): string[] {
  return choices.filter((c) => c.hasEmail && c.why === CREW_ROLE_WORD['pilot']).map((c) => c.key);
}

/** The register people and sign-ins of a choice, as `email_send` takes them. */
export function recipientIds(keys: readonly string[]): {
  readonly people: string[];
  readonly users: string[];
} {
  const people: string[] = [];
  const users: string[] = [];
  for (const k of new Set(keys)) {
    if (k.startsWith('person:')) people.push(k.slice(7));
    else if (k.startsWith('user:')) users.push(k.slice(5));
  }
  return { people, users };
}

/** What `email_send` answered, in words for the person who pressed Send. */
export function emailSentWords(
  r: {
    readonly queued: number;
    readonly without_email?: readonly string[];
  },
  /** The sender's organisation (decision 890); absent: "No Reply via Teslyn". */
  organisation?: string | null,
): string {
  const missing = r.without_email ?? [];
  const sent =
    r.queued === 1 ? 'The email is on its way to 1 person' : `The email is on its way to ${r.queued} people`;
  const tail = missing.length
    ? ` Not sent to ${missing.join(', ')}: Teslyn has no email address for them.`
    : '';
  return `${sent}, from "${appEmailSenderName(organisation)}" with replies to you.${tail}`;
}

/** The sending states of a queued email, in words (the sent log). */
export const APP_EMAIL_STATUS_WORD: Readonly<Record<string, string>> = {
  pending: 'Waiting to send',
  sent: 'Sent',
  failed: 'Not sent',
  cancelled: 'Not sent: unpublished',
};
